import { formatDisplayAmount, formatDisplayNumber, formatDisplayDate, formatDisplayDateTime } from "@/Utile/displayFormat.mjs";
export const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export function valueOrDash(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "-";
}

export function formatNumber(value) {
  return formatDisplayNumber(parseNumeric(value, 0));
}

export function formatDate(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  const parsed = new Date(normalized.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return normalized;
  return formatDisplayDate(parsed);
}

export function formatDateTime(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  const parsed = new Date(normalized.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return normalized;
  return formatDisplayDateTime(parsed);
}

export function formatMoney(value, currency = "MDL") {
  const amount = parseNumeric(value, null);
  if (amount === null) return "-";
  return `${formatDisplayAmount(amount)} ${String(currency || "MDL").toUpperCase()}`;
}

export function statusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (!normalized) return "-";
  const key = `ncrm.onboarding.statuses.${normalized}`;
  const translated = this.$t(key);
  return translated === key ? normalized : translated;
}

export const statusColor = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "blocked") return "warning";
  if (normalized === "completed") return "success";
  if (normalized === "canceled") return "error";
  return "primary";
};

export function healthLabel(health) {
  const normalized = String(health || "").trim().toLowerCase();
  if (!normalized) return "-";
  const key = `ncrm.onboarding.health.${normalized}`;
  const translated = this.$t(key);
  return translated === key ? normalized : translated;
}

export const healthColor = (health) => {
  const normalized = String(health || "").trim().toLowerCase();
  if (normalized === "red") return "error";
  if (normalized === "yellow") return "warning";
  return "success";
};

export function taskStatusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (!normalized) return "-";
  const key = `ncrm.activity.statuses.${normalized}`;
  const translated = this.$t(key);
  return translated === key ? normalized : translated;
}

export const taskStatusColor = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "done") return "success";
  if (normalized === "in_progress") return "warning";
  if (normalized === "canceled") return "error";
  return "primary";
};

export function headerProgressTickState(stage, taskIndex) {
  const normalizedIndex = parseNumeric(taskIndex, 0);
  const doneCount = Math.max(0, parseNumeric(stage?.doneCount, 0));
  if (normalizedIndex < doneCount) return "is-done";
  if (parseNumeric(stage?.id, 0) === this.currentStageId && normalizedIndex === doneCount) return "is-current";
  return "is-pending";
}

export function historyEventLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "-";
  const key = `ncrm.onboarding.details.history.eventTypes.${normalized}`;
  const translated = this.$t(key);
  return translated === key ? normalized : translated;
}

export function historyValueLabel(entry) {
  const fromValue = String(entry?.from_value || "").trim();
  const toValue = String(entry?.to_value || "").trim();
  if (!fromValue && !toValue) return this.$t("ncrm.onboarding.details.history.noValueChange");
  return this.$t("ncrm.onboarding.details.history.valueTransition", {
    from: fromValue || "-",
    to: toValue || "-",
  });
}

export const stageWorkTotal = (stage) => {
  return parseNumeric(stage?.task_total, 0) + parseNumeric(stage?.required_field_total, 0);
};

export const stageWorkDone = (stage) => {
  return parseNumeric(stage?.task_done, 0) + parseNumeric(stage?.required_field_completed, 0);
};

export function stageProgress(stage) {
  const total = this.stageWorkTotal(stage);
  if (!total) return 0;
  const done = this.stageWorkDone(stage);
  return Math.max(0, Math.min(100, (done / total) * 100));
}

export function resolveNextStageId() {
  const activeStages = Array.isArray(this.activeStageItems)
    ? this.activeStageItems
    : (Array.isArray(this.stageItems) ? this.stageItems : []).filter(
        (stage) => Number(stage?.is_active) === 1 || stage?.is_active === true || stage?.is_active === undefined
      );
  if (!activeStages.length || !this.currentStageId) return null;

  const sorted = activeStages
    .map((stage) => ({
      id: parseNumeric(stage.id, 0),
      sortOrder: parseNumeric(stage.sort_order, 0),
    }))
    .filter((stage) => stage.id > 0)
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
      return left.id - right.id;
    });

  const currentIndex = sorted.findIndex((stage) => stage.id === this.currentStageId);
  if (currentIndex < 0) return null;
  const next = sorted[currentIndex + 1];
  return next?.id || null;
}
